import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getYardsByCompanyId, getShippingLinesByYards, getGatesByYardIds } from "../../pages/Home/api";
import {
  getScansByYardsAndDate,
  getScansByYardsAndDays,
  getScanByContainerNumber,
  getSessionsByGateIdsAndDate,
} from "../../pages/Report/api";
import { formatDateToISOString } from "../../utils/utils";

const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 6);
  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(today),
  };
};

const isDateStale = (date) => !!date && date < getLocalDateString();

// ── YARD report ────────────────────────────────────────
export const fetchReportData = createAsyncThunk(
  "report/fetchReportData",
  async ({ companyId, filters }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId, "YARD");
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);

      const shippingRes = await getShippingLinesByYards(allYardIds);
      const allShippingLines = shippingRes.status ? shippingRes.data : [];
      const allShippingLineIds = allShippingLines.map((sl) => sl.shipping_line_id);
      const shippingLineOptions = allShippingLines.map((sl) => ({
        label: sl.shipping_line_name,
        value: sl.shipping_line_id,
        yard_id: sl.yard_id,
      }));

      const yardsToUse = filters.yards?.length > 0 ? filters.yards : allYardIds;
      const shippingLinesToUse = filters.useShippingLineFilter ? filters.shippingLines : [];
      const defaultDates = getDefaultDates();

      // If persisted filter mode was "container", don't try to fetch by date/days here —
      // let the container search flow handle it (it dispatches its own fetch on submit).
      let scansData = [];
      let lastFetchedFilters = null;

      if (filters.filterMode === "container" && filters.containerNumber) {
        const response = await getScanByContainerNumber({
          container_number: filters.containerNumber,
          yard_ids: yardsToUse,
          shipping_line_ids: shippingLinesToUse,
        });
        scansData = response.data;
        lastFetchedFilters = {
          filterMode: "container",
          containerNumber: filters.containerNumber,
          yards: yardsToUse,
          shippingLines: shippingLinesToUse,
        };
      } else {
        const payload = { yard_ids: yardsToUse };
        if (filters.useShippingLineFilter && shippingLinesToUse.length > 0)
          payload.shipping_line_ids = shippingLinesToUse;

        if (filters.filterMode === "days" && filters.days) {
          payload.days = Number(filters.days);
          const response = await getScansByYardsAndDays(payload);
          scansData = response.data;
          lastFetchedFilters = {
            yards: yardsToUse,
            filterMode: "days",
            days: filters.days,
            shippingLines: shippingLinesToUse,
            useShippingLineFilter: filters.useShippingLineFilter || false,
          };
        } else {
          const safeStartDate = filters.startDate || defaultDates.startDate;
          const safeEndDate = isDateStale(filters.endDate)
            ? defaultDates.endDate
            : filters.endDate || defaultDates.endDate;

          payload.start_date = formatDateToISOString(safeStartDate, true);
          payload.end_date = formatDateToISOString(safeEndDate, false);
          const response = await getScansByYardsAndDate(payload);
          scansData = response.data;
          lastFetchedFilters = {
            yards: yardsToUse,
            filterMode: "date",
            startDate: payload.start_date,
            endDate: payload.end_date,
            shippingLines: shippingLinesToUse,
            useShippingLineFilter: filters.useShippingLineFilter || false,
          };
        }
      }

      return {
        yardOptions,
        allYardIds,
        allShippingLines,
        allShippingLineIds,
        shippingLineOptions,
        scansData,
        defaultDates,
        lastFetchedFilters,
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchScans = createAsyncThunk(
  "report/fetchScans",
  async (payload, { rejectWithValue }) => {
    try {
      const res = payload.days
        ? await getScansByYardsAndDays(payload)
        : await getScansByYardsAndDate(payload);

      const lastFetchedFilters = payload.days
        ? {
            yards: payload.yard_ids,
            filterMode: "days",
            days: String(payload.days),
            shippingLines: payload.shipping_line_ids || [],
            useShippingLineFilter: !!(payload.shipping_line_ids?.length),
          }
        : {
            yards: payload.yard_ids,
            filterMode: "date",
            startDate: payload.start_date,
            endDate: payload.end_date,
            shippingLines: payload.shipping_line_ids || [],
            useShippingLineFilter: !!(payload.shipping_line_ids?.length),
          };

      return { scansData: res.data, lastFetchedFilters };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ── Container search (yard + shipping-line scoped, no date restriction) ──
export const fetchScanByContainer = createAsyncThunk(
  "report/fetchScanByContainer",
  async (
    { container_number, yard_ids = [], shipping_line_ids = [] },
    { rejectWithValue },
  ) => {
    try {
      const res = await getScanByContainerNumber({
        container_number,
        yard_ids,
        shipping_line_ids,
      });
      const lastFetchedFilters = {
        filterMode: "container",
        containerNumber: container_number,
        yards: yard_ids,
        shippingLines: shipping_line_ids,
      };
      return { scansData: res.data, lastFetchedFilters };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ── WAREHOUSE report ─────────────────────────────────────────────────
export const fetchWarehouseReportData = createAsyncThunk(
  "report/fetchWarehouseReportData",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId, "WAREHOUSE");
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch warehouses");

      const warehouseOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allWarehouseIds = res.data.map((yard) => yard.yard_id);
      const warehouseNameById = Object.fromEntries(
        res.data.map((yard) => [yard.yard_id, yard.yard_name]),
      );

      let gateOptions = [];
      let sessionsData = [];
      const defaultDates = getDefaultDates();

      if (allWarehouseIds.length > 0) {
        const gatesRes = await getGatesByYardIds(allWarehouseIds);
        if (gatesRes.status && Array.isArray(gatesRes.data)) {
          gateOptions = gatesRes.data.map((gate) => ({
            label: gate.gate_name,
            value: gate.gate_id,
            yard_id: gate.yard_id,
            yard_name: warehouseNameById[gate.yard_id] || "Unknown Warehouse",
          }));
        }

        const allGateIds = gateOptions.map((g) => g.value);
        if (allGateIds.length > 0) {
          const sessionsRes = await getSessionsByGateIdsAndDate({
            gate_ids: allGateIds,
            start_date: formatDateToISOString(defaultDates.startDate, true),
            end_date: formatDateToISOString(defaultDates.endDate, false),
          });
          if (sessionsRes.status && Array.isArray(sessionsRes.data)) {
            sessionsData = sessionsRes.data;
          }
        }
      }

      return { warehouseOptions, allWarehouseIds, gateOptions, sessionsData, defaultDates };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// fetch sessions for the selected gates + date range (Warehouse "Apply Filters")
export const fetchWarehouseSessions = createAsyncThunk(
  "report/fetchWarehouseSessions",
  async ({ gate_ids, start_date, end_date }, { rejectWithValue }) => {
    try {
      const res = await getSessionsByGateIdsAndDate({ gate_ids, start_date, end_date });
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch sessions");
      return { sessionsData: res.data };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const reportSlice = createSlice({
  name: "report",
  initialState: {
    // YARD branch
    scansData: [],
    allShippingLines: [],
    shippingLineOptions: [],
    yardOptions: [],
    lastFetchedFilters: null,
    // WAREHOUSE branch
    warehouseOptions: [],
    gateOptions: [],
    sessionsData: [],
    warehouseLoaded: false,
    isLoading: false,
    error: null,
  },
  reducers: {
    setShippingLineOptions: (state, action) => {
      state.shippingLineOptions = action.payload;
    },
    removeScan: (state, action) => {
      state.scansData = state.scansData.filter(
        (scan) => scan.scan_id !== action.payload
      );
    },
    updateScanStageStatus: (state, action) => {
      const { scanId, stageName, reported_to_manager, report_published } = action.payload;
      const scan = state.scansData.find((s) => String(s.scan_id) === String(scanId));
      if (scan) {
        const stage = scan.scan_stages?.find((s) => s.stage_name === stageName);
        if (stage) {
          if (reported_to_manager !== undefined) stage.reported_to_manager = reported_to_manager;
          if (report_published !== undefined) stage.report_published = report_published;
        }
      }
    },
    updateScanContainerNumber: (state, action) => {
      const { scanId, container_number } = action.payload;
      const scan = state.scansData.find((s) => String(s.scan_id) === String(scanId));
      if (scan) {
        scan.container_number = container_number;
      }
    },
    resetReportData: (state) => {
      state.scansData = [];
      state.lastFetchedFilters = null;
    },
    resetReportYards: (state) => {
      state.yardOptions = [];
      state.scansData = [];
      state.allShippingLines = [];
      state.shippingLineOptions = [];
      state.lastFetchedFilters = null;
    },
    resetReportWarehouse: (state) => {
      state.warehouseOptions = [];
      state.gateOptions = [];
      state.sessionsData = [];
      state.warehouseLoaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReportData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.yardOptions = action.payload.yardOptions;
        state.allShippingLines = action.payload.allShippingLines;
        state.shippingLineOptions = action.payload.shippingLineOptions;
        state.scansData = action.payload.scansData;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchReportData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchScans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchScans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scansData = action.payload.scansData;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchScans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchScanByContainer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchScanByContainer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.scansData = action.payload.scansData;
        state.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchScanByContainer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchWarehouseReportData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseReportData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.warehouseLoaded = true;
        state.warehouseOptions = action.payload.warehouseOptions;
        state.gateOptions = action.payload.gateOptions;
        state.sessionsData = action.payload.sessionsData;
      })
      .addCase(fetchWarehouseReportData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchWarehouseSessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionsData = action.payload.sessionsData;
      })
      .addCase(fetchWarehouseSessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setShippingLineOptions,
  removeScan,
  updateScanStageStatus,
  updateScanContainerNumber,
  resetReportData,
  resetReportYards,
  resetReportWarehouse,
} = reportSlice.actions;

export default reportSlice.reducer;